import { useState } from 'react';
import { X, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { useAnnouncement } from '@/hooks/useAnnouncement';
import { useLanguage } from '@/hooks/useLanguage';
import { cn } from '@/lib/utils';

const icons = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
} as const;

export function AnnouncementBanner() {
  const { data: announcement, isLoading } = useAnnouncement();
  const { lang } = useLanguage();
  const [dismissed, setDismissed] = useState(false);

  if (isLoading || !announcement?.enabled || dismissed) return null;

  const message = announcement.messages[lang] || announcement.messages['sv'] || announcement.messages['en'] || '';
  if (!message.trim()) return null;

  const Icon = icons[announcement.type] || Info;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div
        className={cn(
          'relative rounded-lg border p-4 pr-10 shadow-lg backdrop-blur-sm',
          announcement.type === 'error' &&
            'bg-destructive/95 text-destructive-foreground border-destructive/50',
          announcement.type === 'warning' &&
            'bg-primary/95 text-primary-foreground border-primary/50',
          announcement.type === 'info' &&
            'bg-secondary/95 text-secondary-foreground border-secondary/50'
        )}
      >
        <div className="flex gap-3">
          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium leading-relaxed">{message}</p>
        </div>

        {announcement.dismissible && (
          <button
            aria-label="Stäng meddelande"
            onClick={() => setDismissed(true)}
            className={cn(
              'absolute top-3 right-3 rounded-md p-0.5 transition-opacity hover:opacity-70',
              announcement.type === 'error' && 'text-destructive-foreground',
              announcement.type === 'warning' && 'text-primary-foreground',
              announcement.type === 'info' && 'text-secondary-foreground'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
